import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import Header from "@/routes/_layout/header"
import BackButton from "@/components/buttons/back-button"
import useIsMobile from "@/hooks/use-is-mobile"

const FILTER_OPTIONS = {
  pipe_gauge: ["15", "20", "25", "32", "40", "50", "75", "100"],
  pipe_type: ["가정용", "일반용", "대중탕용", "공업용"],
  leak_type: ["A", "B", "C", "D"],
  leak_severity: ["심각", "주의", "미세"]
}

const SimulatorEntryPage = () => {
  const isMobile = useIsMobile()
  const mode = isMobile ? "mobile" : "desktop"

  const navigate = useNavigate()

  const HeaderComponent = Header.default[mode]

  const [filters, setFilters] = useState({
    pipe_gauge: [],
    pipe_type: [],
    leak_type: [],
    leak_severity: [],
    leak_rate: 0,
    leak_mode: "gt" // 'gt'(이상) 또는 'lt'(이하)
  })

  const handleCheckboxChange = (category, value) => {
    setFilters((prev) => {
      const currentList = prev[category]
      if (currentList.includes(value)) {
        return { ...prev, [category]: currentList.filter((item) => item !== value) }
      } else {
        return { ...prev, [category]: [...currentList, value] }
      }
    })
  }

  const handleSearch = () => {
    const params = new URLSearchParams()

    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, v))
      } else {
        params.append(key, value)
      }
    })

    navigate(`/simulator/result?${params.toString()}`)
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <HeaderComponent />
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-10">
        <div className="mb-2">
          <BackButton className="-ml-2 px-2" onClick={() => navigate("/select")} />
        </div>
        {/* 타이틀 영역 */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-3 mb-2">
            <img
              src="/images/select/simulator-logo-b.png"
              alt="logo"
              className="w-12 h-12 object-contain"
            />
            <span className="bg-purple-100 text-purple-700 text-xs font-bold px-3 py-1 rounded-full">
              SIMULATOR
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-800">
            누수 재현 시뮬레이터
          </h1>
          <p className="text-slate-500 mt-2">
            원하는 조건을 선택하여 과거 누수 데이터를 검색해보세요.
          </p>
        </div>

        {/* 필터 컨테이너 */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 md:p-10 space-y-8">
          {/* 1. 구경 필터 */}
          <section>
            <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              📏 구경 <span className="text-sm font-normal text-slate-400">(mm)</span>
            </h3>
            <div className="flex flex-wrap gap-3">
              {FILTER_OPTIONS.pipe_gauge.map((opt) => (
                <label
                  key={opt}
                  className={`cursor-pointer px-4 py-2 rounded-xl border transition-all ${filters.pipe_gauge.includes(opt) ? "bg-blue-600 text-white border-blue-600" : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"}`}
                >
                  <input
                    type="checkbox"
                    className="hidden"
                    onChange={() => handleCheckboxChange("pipe_gauge", opt)}
                    checked={filters.pipe_gauge.includes(opt)}
                  />
                  {opt}
                </label>
              ))}
            </div>
          </section>

          <hr className="border-slate-100" />

          {/* 2. 업종 필터 */}
          <section>
            <h3 className="text-lg font-bold text-slate-800 mb-3">🏢 업종</h3>
            <div className="flex flex-wrap gap-3">
              {FILTER_OPTIONS.pipe_type.map((opt) => (
                <label
                  key={opt}
                  className={`cursor-pointer px-4 py-2 rounded-xl border transition-all ${filters.pipe_type.includes(opt) ? "bg-green-600 text-white border-green-600" : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"}`}
                >
                  <input
                    type="checkbox"
                    className="hidden"
                    onChange={() => handleCheckboxChange("pipe_type", opt)}
                    checked={filters.pipe_type.includes(opt)}
                  />
                  {opt}
                </label>
              ))}
            </div>
          </section>

          <hr className="border-slate-100" />

          {/* 3. 누수 유형 & 심각도 (Grid 배치) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <section>
              <h3 className="text-lg font-bold text-slate-800 mb-3">💧 누수 유형</h3>
              <div className="flex flex-wrap gap-3">
                {FILTER_OPTIONS.leak_type.map((opt) => (
                  <label
                    key={opt}
                    className={`cursor-pointer px-4 py-2 rounded-xl border transition-all ${filters.leak_type.includes(opt) ? "bg-indigo-500 text-white border-indigo-500" : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"}`}
                  >
                    <input
                      type="checkbox"
                      className="hidden"
                      onChange={() => handleCheckboxChange("leak_type", opt)}
                      checked={filters.leak_type.includes(opt)}
                    />
                    {opt}형
                  </label>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-lg font-bold text-slate-800 mb-3">⚠️ 누수 심각도</h3>
              <div className="flex flex-wrap gap-3">
                {FILTER_OPTIONS.leak_severity.map((opt) => (
                  <label
                    key={opt}
                    className={`cursor-pointer px-4 py-2 rounded-xl border transition-all ${filters.leak_severity.includes(opt) ? "bg-red-500 text-white border-red-500" : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"}`}
                  >
                    <input
                      type="checkbox"
                      className="hidden"
                      onChange={() => handleCheckboxChange("leak_severity", opt)}
                      checked={filters.leak_severity.includes(opt)}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </section>
          </div>

          <hr className="border-slate-100" />

          {/* 4. 누수율 필터 */}
          <section>
            <h3 className="text-lg font-bold text-slate-800 mb-3">
              📈 누수율 <span className="text-sm font-normal text-slate-400">(%)</span>
            </h3>
            <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200 w-fit">
              <input
                type="number"
                className="w-24 p-2 text-center font-bold text-lg border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.leak_rate}
                min="0"
                max="100"
                onChange={(e) => setFilters({ ...filters, leak_rate: e.target.value })}
              />
              <span className="text-slate-400">|</span>
              <div className="flex gap-2">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    name="leak-mode"
                    checked={filters.leak_mode === "gt"}
                    onChange={() => setFilters({ ...filters, leak_mode: "gt" })}
                    className="accent-blue-600 w-4 h-4"
                  />
                  <span className="text-slate-700 font-medium">이상</span>
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    name="leak-mode"
                    checked={filters.leak_mode === "lt"}
                    onChange={() => setFilters({ ...filters, leak_mode: "lt" })}
                    className="accent-blue-600 w-4 h-4"
                  />
                  <span className="text-slate-700 font-medium">이하</span>
                </label>
              </div>
            </div>
          </section>

          {/* 검색 버튼 */}
          <div className="pt-6">
            <button
              onClick={handleSearch}
              className="w-full bg-blue-600 text-white text-xl font-bold py-4 rounded-2xl shadow-lg hover:bg-blue-700 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              검색 시작하기
            </button>
          </div>
        </div>
      </main>
    </div>
  )
}

export default SimulatorEntryPage
